import { useCallback, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { UploadCloud } from 'lucide-react';
import { hasFileDrag } from '@/features/grid/dnd';
import { uploadManager } from './UploadManager';
import { cx } from '@/lib/format';

/** Page-level drop target for files and folders (§3.2). Nested board cards stop propagation for their own drops. */
export function DropZone({ boardId, enabled = true, children, className, label = 'Drop to upload' }: { boardId: string; enabled?: boolean; children: ReactNode; className?: string; label?: string }) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const onDragEnter = useCallback(
    (e: DragEvent) => {
      if (!enabled || !hasFileDrag(e)) return;
      e.preventDefault();
      depth.current++;
      setOver(true);
    },
    [enabled],
  );
  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (!enabled || !hasFileDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    [enabled],
  );
  const onDragLeave = useCallback(() => {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  }, []);
  const onDrop = useCallback(
    (e: DragEvent) => {
      depth.current = 0;
      setOver(false);
      if (!enabled || !hasFileDrag(e)) return;
      e.preventDefault();
      void uploadManager.enqueueDataTransfer(e.dataTransfer, boardId);
    },
    [enabled, boardId],
  );
  return (
    <div className={cx('relative min-h-full', className)} onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} data-dropzone={boardId}>
      {children}
      {over && (
        <div className="pointer-events-none absolute inset-2 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-accent bg-accent-soft/70" aria-live="polite">
          <div className="flex items-center gap-2 rounded-lg bg-bg-elev px-4 py-2 text-sm font-medium shadow">
            <UploadCloud size={18} className="text-accent" /> {label}
          </div>
        </div>
      )}
    </div>
  );
}
